import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, pluck } from 'rxjs/operators';
import { SnackBarService } from 'src/app/shared/snackbar.service';
import { DriverStat, Stats } from '../../../types';
import { sortBy } from '../../../utilities/commonSorters';
import { loadSorting, saveSorting } from '../../../utilities/sortingSave';
import { DriversService } from '../../drivers.service';

type DriverSorter = (a: DriverStat, b: DriverStat) => number;

@Component({
  selector: 'lohi-coaching',
  templateUrl: './coaching.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoachingComponent implements OnInit {
  @ViewChild('autopilotCoachingRequest') public autopilotCoachingRequest: TemplateRef<any>;
  public sortBy: keyof DriverStat = 'user_name';
  public sortReverse = true;
  public sortingFunction$$ = new BehaviorSubject<DriverSorter>(getSortBy(this.sortBy, this.sortReverse));
  public driverStats$: Observable<Stats>;
  public drivers$: Observable<DriverStat[]>;
  public targets$: Observable<{}>;
  private coachingDialogRef: MatDialogRef<TemplateRef<any>>;
  public ranks$: Observable<Record<string, number>>;

  constructor(
    private driversService: DriversService,
    private snackbarService: SnackBarService,
    private matDialog: MatDialog,
  ) {}

  public ngOnInit(): void {
    this.driverStats$ = this.driversService.driverCoaching$;
    this.targets$ = this.driversService.activeDriverList$.pipe(
      map((value) => {
        const targets = {};
        value.forEach((value1) => (targets[value1.id] = value1.driverTarget));
        return targets;
      }),
    );
    const sortingData = loadSorting('coaching');
    if (sortingData) {
      this.sortBy = sortingData.sortBy;
      this.sortReverse = sortingData.sortDirection;
      this.sortingFunction$$.next(getSortBy(this.sortBy, this.sortReverse));
    }

    this.drivers$ = combineLatest([this.driverStats$.pipe(pluck('drivers')), this.sortingFunction$$]).pipe(
      filter(([drivers]) => !!drivers),
      map(([value, sortFunc]) => value.sort(sortFunc)),
    );

    this.ranks$ = this.drivers$.pipe(
      map((value) => {
        const ranks = value.slice().sort((a, b) => a.avgRevenue - b.avgRevenue);
        const rankz: Record<string, number> = {};
        for (let i = 0; i < ranks.length; i++) {
          rankz[ranks[i].UserId] = i + 1;
        }
        return rankz;
      }),
    );
  }

  public setSortHeader(key: keyof DriverStat) {
    if (key === this.sortBy) {
      this.sortReverse = !this.sortReverse;
    } else {
      this.sortBy = key;
      this.sortReverse = false;
    }
    saveSorting('coaching', this.sortBy, this.sortReverse);
    this.sortingFunction$$.next(getSortBy(this.sortBy, this.sortReverse));
  }

  public promptAutopilotCoaching() {
    if (this.coachingDialogRef) {
      this.coachingDialogRef.close();
      this.coachingDialogRef = null;
    }

    this.matDialog.open(this.autopilotCoachingRequest, {
      panelClass: ['w-full', 'md:w-1/2', 'lg:w-1/3'],
    });
  }

  public requestInvite() {
    this.snackbarService.showMessage('Thank you for your interest!');
  }

  public getCoachingMessage() {}
}

function getSortBy(key: keyof DriverStat, reverse: boolean): DriverSorter {
  const temp = sortBy(`${reverse ? '-' : ''}${key}`);
  return (a: DriverStat, b: DriverStat) => {
    return temp(a, b);
  };
}
